import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { LoggerService } from '@danangelus/ngx-app-core'
import { ButtonModule } from 'primeng/button'
import { InputGroupModule } from 'primeng/inputgroup'
import { InputGroupAddonModule } from 'primeng/inputgroupaddon'
import { InputTextModule } from 'primeng/inputtext'
import { MessageModule } from 'primeng/message'
import { SessionService } from '../../../core/security/session.service'
import { AppManagerService } from '../../../core/services/app-manager.service'
import { SsoService } from '../../../core/services/sso.service'
import { LoginForm } from '../../../models/form.model'
import { SessionError } from '../../../models/gateway.model'
import { User } from '../../../models/user.model'

const log = LoggerService.get('LoginComponent')

/**
 * Handles the login form and logout notification
 *
 * @author Dan Bennett
 */
@Component({
    selector: 'app-login',
    standalone: true,
    imports: [
        FormsModule,
        InputTextModule,
        InputGroupModule,
        InputGroupAddonModule,
        ButtonModule,
        MessageModule,
    ],
    templateUrl: './login.component.html',
    styleUrl: './login.component.sass',
    encapsulation: ViewEncapsulation.None,
})
export class LoginComponent implements OnInit {

    router = inject(Router)
    route = inject(ActivatedRoute)
    ssoService = inject(SsoService)
    sessionService = inject(SessionService)
    appService = inject(AppManagerService)

    form: LoginForm | undefined
    error: string | undefined
    loading = false

    ngOnInit(): void {
        this.route.params.subscribe((params: any) => {
            this.appService.loadLoginMessage().then(() => log.debug('LoginComponent', 'Login message loaded'))
            if (params.loggedOut) {
                this.sessionService.loggedOut.set(true)
            }
        })
        this.sessionService.user.set(null)
        this.form = {
            username: undefined,
            password: undefined,
            errors: {}
        }
    }

    async login(invalidateOtherSession = false): Promise<void> {
        this.clearLoggedOutMessage()
        if (this.loading) return

        if (!this.form!.username || !this.form!.password) {
            log.debug('login', `Form is not valid!`)

            if (!this.form?.username) {
                this.form!.errors.username = true
            }
            if (!this.form?.password) {
                this.form!.errors.password = true
            }

            this.error = 'FORM_INVALID'
            return
        }
        try {
            this.loading = true
            const response: User | SessionError = await this.ssoService.login(this.form!.username!, this.form!.password!, invalidateOtherSession)
            if (response instanceof SessionError) {
                log.warn(`Error while attempting to login! ${response.error} -> ${response.message}`)
                this.error = response.error
                this.loading = false
                return
            }
            this.sessionService.user.set(response)
        } catch (e) {
            log.error(`Error while attempting to login: ${e}`)
            this.loading = false
            return
        }
        this.loading = false
        await this.router.navigate(['mfa'])
    }

    clearLoggedOutMessage() {
        this.sessionService.loggedOut.set(false)
    }
}
